import { QueryClient, QueryFunction } from "@tanstack/react-query";
import { parseApiError } from "@/lib/api-error";
import { contractorScopeHeaders } from "@/lib/contractor-scope";

/** Retry only failures that can plausibly succeed on a second attempt:
 * network drops and server-declared retryable errors — never caller errors. */
function isRetryableError(error: unknown): boolean {
  const parsed = parseApiError(error);
  if (parsed.status >= 400 && parsed.status < 500) return false;
  return parsed.isNetworkError || parsed.retryable;
}

/** Mutations are not known to be idempotent, so a retry can double-submit
 * (e.g. an order that timed out server-side after being placed). Only retry
 * when the request never got a response at all. */
function isRetryableMutationError(error: unknown): boolean {
  return parseApiError(error).isNetworkError;
}

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    throw new Error(`${res.status}: ${text}`);
  }
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
  getToken?: () => Promise<string | null>,
  extraHeaders?: Record<string, string>,
): Promise<Response> {
  const startTime = Date.now();

  // PC users acting for a contractor scope every request to that contractor.
  // extraHeaders wins: callers that pinned a scope (e.g. the cart hook, whose
  // debounced writes may flush after the active scope has changed) must not be
  // rescoped mid-flight.
  const headers: Record<string, string> = {
    ...contractorScopeHeaders(),
    ...extraHeaders,
  };

  if (data) {
    headers["Content-Type"] = "application/json";
  }

  // Get fresh token if available
  if (getToken) {
    const token = await getToken();
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    } else {
      console.warn("⚠️ No auth token available for request:", url);
    }
  }

  try {
    const res = await fetch(url, {
      method,
      headers,
      body: data ? JSON.stringify(data) : undefined,
      credentials: "include",
    });

    await throwIfResNotOk(res);
    return res;
  } catch (error) {
    const duration = Date.now() - startTime;
    console.error("❌ API Error:", {
      method,
      url,
      error: error instanceof Error ? error.message : String(error),
      duration: `${duration}ms`,
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
  getToken?: () => Promise<string | null>;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior, getToken }) =>
  async ({ queryKey }) => {
    const headers: Record<string, string> = { ...contractorScopeHeaders() };

    if (getToken) {
      const token = await getToken();
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }
    }

    const res = await fetch(queryKey[0] as string, {
      credentials: "include",
      headers,
    });

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

/**
 * QueryClient factory. The app-wide client below is the default; the PC
 * order-scope provider creates one isolated client per contractor so cached
 * data can never bleed between the contractors a PC user switches between.
 */
export function createAppQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        queryFn: getQueryFn({ on401: "throw" }),
        refetchInterval: false,
        refetchOnWindowFocus: true,
        staleTime: 5 * 60 * 1000, // 5 minutes
        // Retry network/retryable errors up to 2 times
        retry: (failureCount, error) =>
          failureCount < 2 && isRetryableError(error),
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000), // Exponential backoff, max 5s
      },
      mutations: {
        // Retry mutations only on network failures, and only once
        retry: (failureCount, error) =>
          failureCount < 1 && isRetryableMutationError(error),
        retryDelay: 1000, // 1 second delay for mutations
      },
    },
  });
}

export const queryClient = createAppQueryClient();
